import {
  Td,
  Text,
  Tr,
  Collapse,
  Box,
  VStack,
  HStack,
  IconButton,
  useDisclosure,
  Spinner,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import useCurrencyText from 'hooks/shared/useCurrencyText';
import ProfitText from 'presentation/components/shared/amount/ProfitText';
import { Color } from 'presentation/theme/colorScheme';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { StrategyDeal } from 'model/modules/strategies/strategies';
import { OperationShort } from 'model/modules/operations/operationShort';
import { OperationType } from 'enums/modules/operations/operationType';
import { useSearchOperationsQuery } from 'rtkQuery/sharespro/queries/operations';
import useOperationTypeName from 'hooks/modules/operations/useOperationTypeName';

const DealsHistoryTableRow: FC<StrategyDeal> = ({
  ticker,
  quantity,
  dateOpen,
  priceOpen,
  dateClose,
  priceClose,
  profit,
  profitPercent,
  clearedProfit,
  clearedProfitPercent,
  feeOpen,
  feeClose,
  feeTotal,
  currency,
  accountId,
}) => {
  const currencyText = useCurrencyText();
  const { t } = useTranslation();
  const { isOpen, onToggle } = useDisclosure();
  const { getOperationTypeName } = useOperationTypeName();

  const formatDate = (timestamp: number): string => {
    return moment(timestamp * 1000).format('DD.MM.YYYY');
  };

  const dateRange = `${formatDate(dateOpen)}-${formatDate(dateClose)}`;
  const quantityText = t('openedPositionsTableQuantityCell', {
    quantity,
    price: currencyText(priceOpen, { symbol: currency.symbol }),
  });

  const { data: operations, isLoading: isLoadingOperations } = useSearchOperationsQuery(
    {
      account_id: accountId,
      ticker_code: ticker,
    },
    {
      skip: !isOpen,
    }
  );

  const getOperationAmountColor = (type: OperationType, amount: number): string => {
    if (type === OperationType.DealFee || type === OperationType.SuccessFee) {
      return Color.Loss;
    }

    return amount >= 0 ? Color.Overlay : Color.Loss;
  };

  const formatOperationAmount = (operation: OperationShort): string => {
    if (operation.type === OperationType.UnlockAsset) {
      return Number(operation.quantity).toFixed(2);
    }
    const value = operation.amount;
    const formattedAmount = currencyText(value, { symbol: operation.currency.symbol });

    if (operation.type === OperationType.DealFee || operation.type === OperationType.SuccessFee) {
      return `-${formattedAmount}`;
    }

    return formattedAmount;
  };

  return (
    <>
      <Tr cursor="pointer" onClick={onToggle}>
        <Td>
          {ticker}
        </Td>
        <Td>
          {dateRange}
        </Td>
        <Td>
          {quantityText}
        </Td>
        <Td>
          <VStack align="start" spacing={1}>
            <ProfitText
              currency={currency}
              profitAmount={clearedProfit}
              profitPercent={clearedProfitPercent}
            />
          </VStack>
        </Td>
        <Td>
          <IconButton
            aria-label={isOpen ? t('hideDetails') : t('showDetails')}
            icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          />
        </Td>
      </Tr>
      <Tr>
        <Td colSpan={5} p={0}>
          <Collapse in={isOpen} animateOpacity>
            <Box
              bg="gray.50"
              p={6}
              borderTop="1px solid"
              borderColor="gray.300"
            >
              <VStack align="start" spacing={4}>
                <HStack>
                  <Text
                    fontWeight="600"
                    color="gray.700"
                    fontSize="md"
                  >
                    {t('dealsHistoryTableOperations')}
                  </Text>
                  <Badge
                    bg="gray.600"
                    color="white"
                    variant="solid"
                    fontSize="xs"
                    px={2}
                    py={1}
                  >
                    {operations?.length ?? 0}
                  </Badge>
                </HStack>

                <Divider borderColor="gray.300" />

                {isLoadingOperations ? (
                  <HStack justify="center" w="100%" py={4}>
                    <Spinner size="sm" color="gray.500" />
                    <Text fontSize="sm" color="gray.600">
                      {t('loadingOperations')}
                    </Text>
                  </HStack>
                ) : operations && operations.length > 0 ? (
                  <VStack align="start" spacing={3} w="100%">
                    {operations.map((operation, index) => (
                      <Box
                        key={index}
                        w="100%"
                        p={3}
                        bg="white"
                        borderRadius="8px"
                        border="1px solid"
                        borderColor="gray.300"
                        boxShadow="0 1px 3px rgba(0, 0, 0, 0.1)"
                        _hover={{
                          transform: 'translateX(4px)',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                        }}
                        transition="all 0.2s ease-in-out"
                      >
                        <HStack justify="space-between" w="100%">
                          <VStack align="start" spacing={1}>
                            <Text
                              fontWeight="600"
                              color="gray.700"
                              fontSize="sm"
                            >
                              {getOperationTypeName(operation.type)}
                            </Text>
                            <Text
                              fontSize="xs"
                              color="gray.500"
                            >
                              {moment(operation.date).format('DD.MM.YYYY HH:mm')}
                            </Text>
                          </VStack>
                          <Text
                            fontWeight="700"
                            color={getOperationAmountColor(operation.type, operation.amount)}
                            fontSize="sm"
                          >
                            {formatOperationAmount(operation)}
                          </Text>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Box
                    w="100%"
                    p={4}
                    bg="white"
                    borderRadius="8px"
                    border="1px dashed"
                    borderColor="gray.300"
                    textAlign="center"
                  >
                    <Text color="gray.500" fontSize="sm">
                      {t('noOperationsFound')}
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>
          </Collapse>
        </Td>
      </Tr>
    </>
  );
};

export default DealsHistoryTableRow;
